import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import posts, { categories, findPost } from "./posts";
import "./blog.css";

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export const setMeta = (title, description) => {
  document.title = title;
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "description");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", description);
};

export const Cover = ({ post, large }) => (
  <div className={`blog-cover${large ? " blog-cover-large" : ""}`} style={{ "--accent": post.accent }}>
    <span className="blog-cover-ring blog-cover-ring-one" aria-hidden="true" />
    <span className="blog-cover-ring blog-cover-ring-two" aria-hidden="true" />
    <span className="blog-cover-emoji" aria-hidden="true">{post.emoji}</span>
  </div>
);

export const anchorOf = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const Meta = ({ post }) => (
  <p className="blog-meta">
    <span className="blog-chip">{post.category}</span>
    <span>{formatDate(post.date)}</span>
    <span aria-hidden="true">·</span>
    <span>{post.readMins} min read</span>
  </p>
);

/** /blogs — all posts, newest first, filterable by category. */
export const BlogListPage = () => {
  const [category, setCategory] = useState("All");
  useEffect(() => {
    setMeta("Blog · Blox", "Campus stories and guides from Blox: communities, plans, Set Me Up, events and Go Out.");
    window.scrollTo(0, 0);
  }, []);

  const shown = useMemo(() => (category === "All" ? posts : posts.filter((p) => p.category === category)), [category]);
  const [featured, ...rest] = shown;

  return (
    <main className="blog-page">
      <SiteHeader />
      <section className="blog-hero">
        <p className="blog-eyebrow">Ideas worth sharing</p>
        <h1>Blogs</h1>
        <p className="blog-lede">Campus stories, product guides and ideas from the Blox team.</p>
        <div className="blog-filters" role="tablist" aria-label="Filter posts by topic">
          {categories.map((c) => (
            <button key={c} type="button" role="tab" aria-selected={category === c}
              className={`blog-filter${category === c ? " blog-filter-active" : ""}`} onClick={() => setCategory(c)}>
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="blog-list" aria-live="polite">
        {featured && (
          <Link to={`/blogs/${featured.slug}`} className="blog-card blog-card-featured" key={`f-${featured.slug}`}>
            <Cover post={featured} large />
            <div className="blog-card-body">
              <Meta post={featured} />
              <h2>{featured.title}</h2>
              <p>{featured.excerpt}</p>
              <span className="blog-read">Read the post →</span>
            </div>
          </Link>
        )}
        <div className="blog-grid">
          {rest.map((post, i) => (
            <Link to={`/blogs/${post.slug}`} className="blog-card" key={post.slug} style={{ animationDelay: `${i * 60}ms` }}>
              <Cover post={post} />
              <div className="blog-card-body">
                <Meta post={post} />
                <h3>{post.title}</h3>
                <p>{post.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <BlogFooter />
    </main>
  );
};

/** /blogs/:slug — a single post. */
export const BlogPostPage = () => {
  const { slug } = useParams();
  const post = findPost(slug);

  useEffect(() => {
    if (post) setMeta(`${post.title} · Blox`, post.excerpt);
    else setMeta("Post not found · Blox", "This post doesn't exist.");
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) {
    return (
      <main className="blog-page">
        <SiteHeader />
        <section className="blog-hero">
          <p className="blog-eyebrow">Blog</p>
          <h1 className="blog-title-small">Post not found</h1>
          <p className="blog-lede">It may have moved. Have a look at our latest posts instead.</p>
          <Link to="/blogs" className="blog-button">Back to all posts</Link>
        </section>
      </main>
    );
  }

  const sections = post.blocks.filter((b) => b.h).map((b) => b.h);
  const sameTopic = posts.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 3);
  const related = posts.filter((p) => p.slug !== post.slug && p.category === post.category).concat(posts.filter((p) => p.category !== post.category)).slice(0, 3);

  return (
    <main className="blog-page">
      <SiteHeader />
      <article className="blog-article">
        <header className="blog-article-head">
          <Link to="/blogs" className="blog-back">← All posts</Link>
          <Meta post={post} />
          <h1 className="blog-article-title">{post.title}</h1>
          <p className="blog-article-lede">{post.excerpt}</p>
        </header>
        <Cover post={post} large />

        <div className="blog-article-layout">
          <div className="blog-body">
            {post.blocks.map((b, i) => {
              if (b.h) return <h2 key={i} id={anchorOf(b.h)}>{b.h}</h2>;
              if (b.list) return <ul key={i}>{b.list.map((item) => <li key={item}>{item}</li>)}</ul>;
              if (b.quote) return <blockquote key={i}>{b.quote}</blockquote>;
              if (b.tip) return (
                <aside key={i} className="blog-tip">
                  {b.title && <strong>{b.title}</strong>}
                  <span>{b.tip}</span>
                </aside>
              );
              return <p key={i}>{b.p}</p>;
            })}
          </div>

          <aside className="blog-sidebar">
            {sections.length > 0 && (
              <nav className="blog-side-card" aria-label="In this post">
                <p className="blog-side-title">In this post</p>
                <ol className="blog-toc">
                  {sections.map((h) => (
                    <li key={h}><a href={`#${anchorOf(h)}`}>{h}</a></li>
                  ))}
                </ol>
              </nav>
            )}

            <div className="blog-side-card blog-side-dark">
              <p className="blog-side-title">Get the app</p>
              <p className="blog-side-copy">Your campus, in your pocket.</p>
              <div className="blog-side-buttons">
                <a className="blog-button blog-button-light" href="https://apps.apple.com/us/app/blox-your-campus-in-a-pocket/id6498945325" target="_blank" rel="noreferrer">App Store</a>
                <a className="blog-button blog-button-light" href="https://play.google.com/store/apps/details?id=com.schaffen.giggle" target="_blank" rel="noreferrer">Google Play</a>
              </div>
            </div>

            {sameTopic.length > 0 && (
              <div className="blog-side-card">
                <p className="blog-side-title">More on {post.category}</p>
                <ul className="blog-side-posts">
                  {sameTopic.map((p) => (
                    <li key={p.slug}>
                      <Link to={`/blogs/${p.slug}`}>
                        <span className="blog-side-emoji" style={{ background: p.accent }} aria-hidden="true">{p.emoji}</span>
                        <span>{p.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </article>

      <section className="blog-related">
        <h2>Keep reading</h2>
        <div className="blog-grid">
          {related.map((p) => (
            <Link to={`/blogs/${p.slug}`} className="blog-card" key={p.slug}>
              <Cover post={p} />
              <div className="blog-card-body">
                <Meta post={p} />
                <h3>{p.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <BlogFooter />
    </main>
  );
};

export const BlogFooter = () => (
  <footer className="blog-footer">
    <span>© {new Date().getFullYear()} Blox Technologies Pvt Ltd</span>
    <span>
      <Link to="/privacy-policy">Privacy</Link> · <Link to="/terms-of-service">Terms</Link> · <Link to="/campus-partner">Campus Partner</Link>
    </span>
  </footer>
);
